import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import api from "../../lib/api";
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, startOfDay, endOfDay, addDays, addWeeks, addMonths, eachDayOfInterval, isSameMonth, isSameDay, parseISO } from "date-fns";
import { CaretLeft, CaretRight, Warning } from "@phosphor-icons/react";
import { API } from "../../App";
import { fetchStatuses, statusBadgeCls } from "./FollowupStatusSelect";

const PRIORITY_COLORS = { hot: "#DC2626", warm: "#F59E0B", cold: "#10B981" };

function fmtMoney(n) {
  const v = Number(n) || 0;
  if (v === 0) return "—";
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(2)}Cr`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(2)}L`;
  if (v >= 1000) return `₹${(v / 1000).toFixed(1)}k`;
  return `₹${v.toFixed(0)}`;
}

function inrFull(n) {
  return `₹${(Number(n) || 0).toLocaleString("en-IN")}`;
}

const VIEWS = ["month", "week", "day"];

export default function LeadsFollowUpCalendar({ user, onEventClick }) {
  const [cursor, setCursor] = useState(() => new Date());
  const [view, setView] = useState("month");
  const [events, setEvents] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(false);

  const { fromDate, toDate, gridStart, gridEnd, days } = useMemo(() => {
    if (view === "day") {
      const s = startOfDay(cursor); const e = endOfDay(cursor);
      return { fromDate: format(s, "yyyy-MM-dd"), toDate: format(e, "yyyy-MM-dd"), gridStart: s, gridEnd: e, days: [s] };
    }
    if (view === "week") {
      const s = startOfWeek(cursor, { weekStartsOn: 1 });
      const e = endOfWeek(cursor, { weekStartsOn: 1 });
      return { fromDate: format(s, "yyyy-MM-dd"), toDate: format(e, "yyyy-MM-dd"), gridStart: s, gridEnd: e, days: eachDayOfInterval({ start: s, end: e }) };
    }
    const mStart = startOfMonth(cursor); const mEnd = endOfMonth(cursor);
    const s = startOfWeek(mStart, { weekStartsOn: 1 });
    const e = endOfWeek(mEnd, { weekStartsOn: 1 });
    return { fromDate: format(s, "yyyy-MM-dd"), toDate: format(e, "yyyy-MM-dd"), gridStart: s, gridEnd: e, days: eachDayOfInterval({ start: s, end: e }) };
  }, [cursor, view]);

  const refresh = useCallback(async () => {
    if (!user?.user_id) return;
    setLoading(true);
    try {
      const r = await api.get(`${API}/leads/follow-ups/calendar`, {
        params: {
          from_date: fromDate, to_date: toDate,
          requester_user_id: user.user_id,
          requester_name: user.name,
          requester_role: user.role,
        },
      });
      setEvents(r.data?.events || []);
      setTotals(r.data?.totals || {});
    } catch (e) {
      setEvents([]); setTotals({});
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, user?.user_id, user?.name, user?.role]);

  useEffect(() => { refresh(); }, [refresh]);

  const eventsByDay = useMemo(() => {
    const m = {};
    for (const e of events) {
      const iso = e.next_follow_up_at;
      if (!iso) continue;
      let d;
      try { d = parseISO(iso); } catch { continue; }
      const key = format(d, "yyyy-MM-dd");
      (m[key] = m[key] || []).push(e);
    }
    return m;
  }, [events]);

  const goPrev = () => setCursor((c) => view === "day" ? addDays(c, -1) : view === "week" ? addWeeks(c, -1) : addMonths(c, -1));
  const goNext = () => setCursor((c) => view === "day" ? addDays(c, 1) : view === "week" ? addWeeks(c, 1) : addMonths(c, 1));
  const goToday = () => setCursor(new Date());

  const headerTitle = view === "day" ? format(cursor, "EEEE, d MMMM yyyy")
    : view === "week" ? `${format(gridStart, "d MMM")} – ${format(gridEnd, "d MMM yyyy")}`
    : format(cursor, "MMMM yyyy");

  return (
    <div data-testid="leads-followup-calendar" className="space-y-3">
      {/* Stat strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="leads-fu-calendar-stats">
        <div className="border border-slate-200 bg-white px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Due Today</div>
          <div className="text-xl font-bold text-slate-800" data-testid="stat-due-today">{totals.count_today ?? 0}</div>
          <div className="text-[10px] text-slate-500">{inrFull(totals.today)}</div>
        </div>
        <div className="border border-rose-200 bg-rose-50 px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider text-rose-700">Overdue</div>
          <div className="text-xl font-bold text-rose-700" data-testid="stat-overdue">{totals.count_overdue ?? 0}</div>
          <div className="text-[10px] text-rose-600">{inrFull(totals.overdue)}</div>
        </div>
        <div className="border border-blue-200 bg-blue-50 px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider text-blue-700">Upcoming</div>
          <div className="text-xl font-bold text-blue-700" data-testid="stat-upcoming">{totals.count_upcoming ?? 0}</div>
          <div className="text-[10px] text-blue-600">{inrFull(totals.upcoming)}</div>
        </div>
        <div className="border border-emerald-200 bg-emerald-50 px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider text-emerald-700">Total Estimated</div>
          <div className="text-xl font-bold text-emerald-700" data-testid="stat-total-estimated">{fmtMoney(totals.filtered)}</div>
          <div className="text-[10px] text-emerald-600">{inrFull(totals.filtered)}</div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-2 border-b border-slate-200 pb-2">
        <div className="flex items-center gap-1">
          <button data-testid="leads-cal-prev" onClick={goPrev} className="p-2 hover:bg-slate-100"><CaretLeft size={14} /></button>
          <button data-testid="leads-cal-today" onClick={goToday} className="px-3 py-1.5 text-xs uppercase tracking-wider font-bold border border-slate-300 hover:bg-slate-50">Today</button>
          <button data-testid="leads-cal-next" onClick={goNext} className="p-2 hover:bg-slate-100"><CaretRight size={14} /></button>
          <div className="ml-3 text-sm font-bold text-slate-800" data-testid="leads-cal-title">{headerTitle}</div>
        </div>
        <div className="flex gap-1">
          {VIEWS.map((v) => (
            <button key={v} data-testid={`leads-cal-view-${v}`} onClick={() => setView(v)}
              className={`px-3 py-1.5 text-xs uppercase tracking-wider font-bold border ${view === v ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}>
              {v}
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      {loading && <div className="text-sm text-slate-400">Loading follow-ups…</div>}
      {!loading && events.length === 0 && <div className="text-sm text-slate-400 italic" data-testid="leads-cal-empty">No follow-ups in this period.</div>}
      {view === "month" && (
        <div className="grid grid-cols-7 border-t border-l border-slate-200">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
            <div key={d} className="text-[10px] uppercase tracking-wider font-bold text-slate-500 px-2 py-1 border-r border-b border-slate-200 bg-slate-50">{d}</div>
          ))}
          {days.map((d) => {
            const key = format(d, "yyyy-MM-dd");
            const isToday = isSameDay(d, new Date());
            const inMonth = isSameMonth(d, cursor);
            const dayEvents = eventsByDay[key] || [];
            return (
              <div key={key} className={`min-h-[88px] border-r border-b border-slate-200 p-1 text-xs ${inMonth ? "bg-white" : "bg-slate-50/50"} ${isToday ? "ring-1 ring-[#002FA7] ring-inset" : ""}`}>
                <div className="text-[10px] text-slate-500 font-bold mb-1">{format(d, "d")}</div>
                <div className="space-y-1">
                  {dayEvents.slice(0, 3).map((e) => <EventCard key={e.follow_up_id} ev={e} onClick={onEventClick} />)}
                  {dayEvents.length > 3 && <div className="text-[10px] text-slate-500">+{dayEvents.length - 3} more</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {view === "week" && (
        <div className="grid grid-cols-7 border-t border-l border-slate-200">
          {days.map((d) => {
            const key = format(d, "yyyy-MM-dd");
            const dayEvents = eventsByDay[key] || [];
            const isToday = isSameDay(d, new Date());
            return (
              <div key={key} className={`min-h-[300px] border-r border-b border-slate-200 p-2 ${isToday ? "ring-1 ring-[#002FA7] ring-inset" : ""}`}>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">{format(d, "EEE d")}</div>
                <div className="space-y-1">
                  {dayEvents.map((e) => <EventCard key={e.follow_up_id} ev={e} onClick={onEventClick} full />)}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {view === "day" && (
        <div className="border border-slate-200 p-3 min-h-[300px]">
          <div className="space-y-1">
            {(eventsByDay[format(cursor, "yyyy-MM-dd")] || []).map((e) => <EventCard key={e.follow_up_id} ev={e} onClick={onEventClick} full />)}
            {(eventsByDay[format(cursor, "yyyy-MM-dd")] || []).length === 0 && <div className="text-sm text-slate-400 italic">No follow-ups scheduled for this day.</div>}
          </div>
        </div>
      )}
    </div>
  );
}

function EventCard({ ev, onClick, full = false }) {
  const color = PRIORITY_COLORS[(ev.priority || "warm").toLowerCase()] || "#475569";
  const time = ev.next_follow_up_at ? (() => { try { return format(parseISO(ev.next_follow_up_at), "HH:mm"); } catch { return ""; } })() : "";
  const [statusMeta, setStatusMeta] = useState(null);
  useEffect(() => {
    if (!ev.status) return;
    fetchStatuses().then((rows) => {
      const match = rows.find((s) => s.slug === ev.status || s.name === ev.status);
      if (match) setStatusMeta(match);
    });
  }, [ev.status]);
  const label = statusMeta?.name || (ev.status ? String(ev.status).replace(/_/g, " ") : (ev.outcome || "—"));
  return (
    <button
      data-testid={`leads-cal-event-${ev.follow_up_id}`}
      onClick={() => onClick && onClick(ev)}
      className={`w-full text-left p-1.5 border-l-2 hover:bg-slate-50 transition-colors ${ev.is_overdue ? "border-rose-200 bg-rose-50/40" : "border-slate-200 bg-white"}`}
      style={{ borderLeftColor: color }}
      title={`${ev.lead_name} · ${ev.company_name || ""}`}
    >
      <div className="flex items-center gap-1 text-[10px] text-slate-500">
        {time && <span className="font-mono">{time}</span>}
        {ev.is_overdue && <Warning size={10} weight="fill" className="text-rose-500" />}
        <span className="ml-auto font-bold" style={{ color }}>{fmtMoney(ev.estimated_amount)}</span>
      </div>
      <div className="text-xs font-bold text-slate-800 truncate">{ev.lead_name}</div>
      {full && ev.company_name && <div className="text-[10px] text-slate-500 truncate">{ev.company_name}</div>}
      {full && (
        <div className="text-[10px] text-slate-500 truncate flex items-center gap-1.5">
          <span>{ev.assigned_to || "—"}</span>
          <span>·</span>
          <span className={`${statusBadgeCls(statusMeta?.color)} px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-bold`}>
            {label}
          </span>
        </div>
      )}
    </button>
  );
}
